import { LinkedInWizardData } from "@/pages/LinkedInWizard";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

interface LinkedInWizardStep3Props {
  data: LinkedInWizardData;
  updateData: (updates: Partial<LinkedInWizardData>) => void;
  onNext: () => void;
  onPrev: () => void;
}

const USP_OPTIONS = [
  "Proven track record with results",
  "Rare cross-industry skills",
  "Deep niche expertise", 
  "Turnaround or transformation expert",
  "High-impact metrics",
  "High-level connections",
  "Proprietary method or system",
  "Mission-driven or values-led",
  "Known for customer loyalty or success",
  "First-mover in a new market",
  "Credentials, awards, or media",
  "None / unsure"
];

export function LinkedInWizardStep3({ data, updateData }: LinkedInWizardStep3Props) {
  const toggleUSP = (usp: string) => {
    const currentUSPs = data.uniqueSellingPoints || [];
    const isSelected = currentUSPs.includes(usp);
    
    if (isSelected) {
      updateData({
        uniqueSellingPoints: currentUSPs.filter(u => u !== usp)
      });
    } else {
      updateData({
        uniqueSellingPoints: [...currentUSPs, usp]
      });
    }
  };

  const hasNoneUnsure = data.uniqueSellingPoints?.includes("None / unsure");

  return (
    <div className="space-y-6">
      {/* Step Number and Header */}
      <div className="relative">
        <div className="absolute -top-2 -left-2">
          <div className="w-12 h-12 bg-linkedin rounded-full flex items-center justify-center shadow-linkedin">
            <span className="text-2xl font-bold text-white">3</span>
          </div>
        </div>
        <div className="ml-12">
          <h2 className="text-2xl font-bold mb-2 text-linkedin">What makes you stand out from others in your field?</h2>
          <p className="text-linkedin/70 mb-6">
            Select all that apply, or describe what makes you unique in the text area below.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {USP_OPTIONS.map((usp) => {
          const isSelected = data.uniqueSellingPoints?.includes(usp);
          
          return (
            <Card 
              key={usp}
              className={`p-4 cursor-pointer transition-all hover:shadow-md border-linkedin/20 ${
                isSelected 
                  ? 'border-green-500 bg-green-50 dark:bg-green-950 ring-2 ring-green-200 shadow-md' 
                  : 'border-linkedin/30 bg-linkedin-light/10 hover:border-linkedin/50 hover:bg-linkedin-light/20'
              }`}
              onClick={() => toggleUSP(usp)}
            >
              <div className="flex items-center justify-between">
                <span className={`text-sm font-medium ${isSelected ? 'text-green-800 dark:text-green-200' : 'text-linkedin dark:text-linkedin'}`}>{usp}</span>
                {isSelected && (
                  <Badge variant="default" className="bg-green-600 hover:bg-green-700 text-white">
                    Selected
                  </Badge>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {hasNoneUnsure && (
        <div className="space-y-2">
          <Label htmlFor="custom-usp" className="text-sm font-medium text-linkedin">
            Describe what you love doing or what people always praise you for:
          </Label>
          <Textarea
            id="custom-usp"
            placeholder="Describe your unique value proposition..."
            value={data.customUSP || ""}
            onChange={(e) => updateData({ customUSP: e.target.value })}
            className="min-h-[100px] border-linkedin/20 focus:ring-linkedin focus:border-linkedin"
          />
        </div>
      )}

      {data.uniqueSellingPoints && data.uniqueSellingPoints.length > 0 && (
        <div className="mt-6 p-4 bg-linkedin-light dark:bg-linkedin-dark/20 rounded-lg border border-linkedin/20">
          <p className="text-sm font-medium text-linkedin mb-2">
            Selected Unique Selling Points ({data.uniqueSellingPoints.length}):
          </p>
          <div className="flex flex-wrap gap-2">
            {data.uniqueSellingPoints.map((usp) => (
              <Badge key={usp} variant="secondary" className="text-xs bg-white border-linkedin/20">
                {usp}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}